// I pezzi della procedura «Nuova attività» per S4 (tela, artboard 36 e 49): la condizione di
// «quando succede», legata allo strumento che la fa partire (memo o una connessione Gmail), e il
// token dell'hook, che si vede una volta sola dopo il salvataggio.
import { useState } from 'react';
import { Banner } from '../../components/Banner';
import { Button } from '../../components/Button';
import { Field, TextField } from '../../components/Field';
import { Select } from '../../components/Select';
import { Switch } from '../../components/Switch';
import type { MemoWatch, RoutineCondition, RoutineOptions, ToolGroup } from '../../lib/api';
import { Choice } from './Choice';
import { ApiFields } from './ConditionApi';
import { GmailFields } from './ConditionGmail';
import { MemoScopeFields } from './ConditionMemo';

export const DEFAULT_CONDITION: RoutineCondition = {
  source: 'memo',
  watch: '',
  metric: 'anomalies',
  op: '>=',
  value: 1,
  every_minutes: 5,
  sequence: true,
};
export const DEFAULT_OPTIONS: RoutineOptions = {
  kind: 'text',
  profile: null,
  tools: [],
  push: 'project',
  pullRequest: 'project',
  notify: [],
};

const EVERY = [1, 5, 15, 30, 60, 180, 720, 1440];
const every = (m: number) =>
  m < 60 ? `Ogni ${m} min` : m === 60 ? 'Ogni ora' : `Ogni ${m / 60} ore`;

export function ConditionEditor({
  value,
  onChange,
  watched,
  mailboxes,
  apis = [],
}: {
  value: RoutineCondition;
  onChange: (c: RoutineCondition) => void;
  watched: MemoWatch[];
  mailboxes: ToolGroup[];
  /** le connessioni API di Strumenti */
  apis?: ToolGroup[];
}) {
  const set = (patch: Partial<RoutineCondition>) => onChange({ ...value, ...patch });
  const everySelect = (
    <Field label="Controlla">
      <Select
        label="Ogni quanto si controlla"
        value={String(value.every_minutes)}
        onChange={(v) => set({ every_minutes: Number(v) })}
        options={EVERY.map((m) => ({ value: String(m), label: every(m) }))}
        width="100%"
      />
    </Field>
  );
  const tools = (
    <div style={{ display: 'grid', gap: 8 }}>
      <span className="t-strong">Strumento</span>
      <div role="radiogroup" aria-label="Strumento" className="rw-kinds">
        {mailboxes.map((g) => (
          <Choice
            key={g.id}
            on={
              (value.source === 'gmail' || value.source === 'calendar') && value.connection === g.id
            }
            icon="mail"
            title={g.name}
            hint={`Gmail${g.account ? ` · ${g.account}` : ''} · email nuove, etichette, calendario`}
            onPick={() =>
              set({
                source: 'gmail',
                connection: g.id,
                query: value.query || 'in:inbox',
                batch: value.batch ?? 'each',
                remember_days: value.remember_days ?? 7,
              })
            }
          />
        ))}
        {apis.map((g) => (
          <Choice
            key={g.id}
            on={value.source === 'api' && value.connection === g.id}
            icon="globe"
            title={g.name}
            hint={`API · ${g.url ?? ''}`}
            onPick={() =>
              set({
                source: 'api',
                connection: g.id,
                op: value.op === '>=' ? '>' : value.op,
                fire: value.fire ?? 'rising',
              })
            }
          />
        ))}
        <Choice
          on={value.source === 'memo'}
          icon="brain"
          title="memo"
          hint="Flow · anomalie e backlog"
          onPick={() => set({ source: 'memo' })}
        />
      </div>
      <span className="t-caption dim">
        Le connessioni di Strumenti che sanno far partire un’attività. Ognuna ha i suoi campi.
      </span>
    </div>
  );
  if (value.source === 'api')
    return (
      <div style={{ display: 'grid', gap: 14 }}>
        {tools}
        <ApiFields value={value} set={set} everySelect={everySelect} />
      </div>
    );
  if (value.source === 'gmail' || value.source === 'calendar')
    return (
      <div style={{ display: 'grid', gap: 14 }}>
        {tools}
        <GmailFields
          value={value}
          set={set}
          everySelect={everySelect}
          calendar={mailboxes.some(
            (g) => g.id === value.connection && g.tools.some((t) => t.name === 'list_events'),
          )}
        />
      </div>
    );
  // un'attività di prima ha solo la voce di controllo: spazio e progetto si leggono da lì
  const known = watched.find((w) => w.id === value.watch);
  const space = value.space ?? known?.space ?? '';
  const project = value.project ?? known?.project ?? '';
  return (
    <div style={{ display: 'grid', gap: 14 }}>
      {tools}
      <div className="rp-tool-box">
        <MemoScopeFields value={value} space={space} project={project} set={set} />
        <div className="rw-grid">
          <Field label="Misura">
            <Select
              label="Misura"
              value={value.metric}
              onChange={(metric) => set({ metric: metric as RoutineCondition['metric'] })}
              options={[
                { value: 'anomalies', label: 'Anomalie aperte' },
                { value: 'backlog', label: 'Ticket in backlog' },
              ]}
              width="100%"
            />
          </Field>
          <Field label="Confronto">
            <Select
              label="Confronto"
              value={value.op}
              onChange={(op) => set({ op: op as RoutineCondition['op'] })}
              options={['>=', '>', '=', '<', '<='].map((o) => ({
                value: o,
                label: o.replace('>=', '≥').replace('<=', '≤'),
              }))}
              width="100%"
            />
          </Field>
          <Field label="Valore">
            <TextField
              value={String(value.value)}
              onChange={(v) => set({ value: Math.max(0, Math.min(10_000, Number(v) || 0)) })}
            />
          </Field>
          {everySelect}
        </div>
        <Switch
          label="Una alla volta, in sequenza"
          hint="Apre una sessione per l'anomalia più vecchia (ovunque sia nell'ambito) e la segna «in lavorazione» su memo; la prossima al giro dopo."
          checked={value.sequence}
          onChange={(sequence) => set({ sequence })}
        />
      </div>
    </div>
  );
}

export function HookReveal({
  token,
  path,
  onDone,
}: {
  token: string;
  path: string;
  onDone: () => void;
}) {
  const [copied, setCopied] = useState(false);
  const url = `${window.location.origin}${path}`;
  const copy = () => {
    void navigator.clipboard?.writeText(
      `curl -X POST ${url} -H 'X-PopTown-Hook: ${token}' -d '{}'`,
    );
    setCopied(true);
  };
  return (
    <div style={{ display: 'grid', gap: 10 }}>
      <Banner kind="warn">
        Il token si vede solo adesso: copialo nel sistema che chiamerà l’hook.
      </Banner>
      <Field label="Indirizzo (POST)">
        <TextField value={url} onChange={() => undefined} mono />
      </Field>
      <Field label="Intestazione X-PopTown-Hook">
        <TextField value={token} onChange={() => undefined} mono />
      </Field>
      <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
        <Button icon={copied ? 'check' : 'copy'} onClick={copy}>
          {copied ? 'Copiato' : 'Copia il comando curl'}
        </Button>
        <Button variant="primary" onClick={onDone}>
          Fatto
        </Button>
      </div>
    </div>
  );
}
